import { useState, useMemo, useEffect, useRef } from 'react'
import { Search, ChevronDown, ChevronUp, MoreVertical, X, Phone, MessageCircle } from 'lucide-react'
import AsoEbiBulkMessage, { BulkActionBar } from './AsoEbiBulkMessage'

// ── Shared helpers ────────────────────────────────────────────
const STATUS_META = {
  pending:   { label:'Unpaid',    bg:'rgba(220,38,38,0.08)',   color:'#DC2626',  dot:'#DC2626' },
  paid:      { label:'Paid',      bg:'rgba(244,179,0,0.12)',   color:'#B8920A',  dot:'#F4B300' },
  collected: { label:'Collected', bg:'rgba(155,175,155,0.18)', color:'#5A7A5A',  dot:'#9BAF9B' },
  cancelled: { label:'Cancelled', bg:'#F3F0EA',                color:'#BCBCBC',  dot:'#BCBCBC' },
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.cancelled
  return (
    <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', fontWeight:700, borderRadius:100, padding:'3px 10px', background:m.bg, color:m.color, whiteSpace:'nowrap' }}>
      {m.label}
    </span>
  )
}

function StatusDot({ status }) {
  const m = STATUS_META[status] || STATUS_META.cancelled
  return <span style={{ width:8, height:8, borderRadius:'50%', background:m.dot, display:'inline-block', flexShrink:0 }}/>
}

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024)
  useEffect(() => {
    const fn = () => setW(window.innerWidth)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])
  return w
}

// ── Main component ────────────────────────────────────────────
export default function AsoEbiOrdersTable({ orders, points, wedding, asoEbi, updatingOrder, onUpdateStatus, onSendReminder }) {
  const width = useWindowWidth()
  const isDesktop = width >= 1024
  const isTablet  = width >= 768 && width < 1024

  // ── Shared state (survives breakpoint changes) ────────────
  const [search, setSearch]     = useState('')
  const [filter, setFilter]     = useState('all')
  const [sortField, setSortField] = useState(null)
  const [sortDir, setSortDir]   = useState('asc')
  const [selected, setSelected] = useState(new Set())

  const [remindedSet, setRemindedSet] = useState(new Set())
  const [showBulkModal, setShowBulkModal] = useState(false)

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortField(field); setSortDir('asc') }
  }

  const filtered = useMemo(() => {
    let r = orders.filter(o => {
      if (search && !o.guest_name?.toLowerCase().includes(search.toLowerCase()) && !o.guest_phone?.includes(search)) return false
      if (filter === 'unpaid') return o.status === 'pending'
      if (filter === 'not_collected') return o.status === 'paid'
      if (filter.startsWith('point:')) return o.collection_point === filter.slice(6)
      return true
    })
    if (sortField) r = [...r].sort((a,b) => { const v = (a[sortField]||0) - (b[sortField]||0); return sortDir==='asc'?v:-v })
    return r
  }, [orders, search, filter, sortField, sortDir])

  const bulkGuests = filter === 'unpaid'
    ? filtered.filter(o => o.status === 'pending')
    : filter === 'not_collected'
    ? filtered.filter(o => o.status === 'paid')
    : []

  const allSelected = filtered.length > 0 && filtered.every(o => selected.has(o.id))
  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(filtered.map(o => o.id)))
  }
  const toggleOne = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })

  const uniquePoints = [...new Set(orders.map(o => o.collection_point).filter(Boolean))]
  const filterPills = [
    { id:'all', label:'All' },
    { id:'not_collected', label:'Not Collected' },
    ...uniquePoints.map(p => ({ id:`point:${p}`, label:p }))
  ]

  const sharedProps = { filtered, search, setSearch, filter, setFilter, filterPills, sortField, sortDir, toggleSort, selected, setSelected, toggleAll, toggleOne, allSelected, onUpdateStatus, onSendReminder, wedding, asoEbi, updatingOrder, remindedSet, bulkGuests, onOpenBulk: ()=>setShowBulkModal(true) }

  return (
    <>
      {isDesktop ? <DesktopView {...sharedProps}/> : isTablet ? <TabletView {...sharedProps}/> : <MobileView {...sharedProps}/>}
      {showBulkModal && (
        <AsoEbiBulkMessage
          filter={filter}
          guests={bulkGuests}
          asoEbi={asoEbi}
          wedding={wedding}
          remindedSet={remindedSet}
          onClose={(sentIds) => { setRemindedSet(prev => new Set([...prev, ...sentIds])); setShowBulkModal(false) }}
          onMarkReminded={(id) => setRemindedSet(prev => new Set([...prev, id]))}
        />
      )}
    </>
  )
}

// ────────────────────────────────────────────────────────────
// DESKTOP VIEW
// ────────────────────────────────────────────────────────────
function DesktopView({ filtered, search, setSearch, filter, setFilter, filterPills, sortField, sortDir, toggleSort, selected, setSelected, toggleAll, toggleOne, allSelected, onUpdateStatus, onSendReminder, wedding, asoEbi, updatingOrder, remindedSet, bulkGuests, onOpenBulk }) {
  const [openMenu, setOpenMenu] = useState(null)
  const [menuPos, setMenuPos]   = useState({ top:0, right:0 })
  const menuRef = useRef()

  useEffect(() => {
    const fn = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null) }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  function handleOpenMenu(id, e) {
    const rect = e.currentTarget.getBoundingClientRect()
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    setOpenMenu(prev => prev === id ? null : id)
  }

  const exportCSV = () => {
    const rows = [['Guest','Phone','Yards','Amount','Status','Collection Point','Date']]
    filtered.forEach(o => rows.push([o.guest_name,o.guest_phone||'',o.yards,o.amount||0,o.status,o.collection_point||'',new Date(o.created_at).toLocaleDateString('en-GB')]))
    const csv = rows.map(r=>r.map(v=>`"${v}"`).join(',')).join('\n')
    const a = document.createElement('a'); a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv); a.download='asoebi-orders.csv'; a.click()
  }

  return (
    <div>
      {/* Top bar */}
      <div style={{ display:'flex', gap:'0.75rem', alignItems:'center', marginBottom:'0.875rem', flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:180 }}>
          <Search size={14} color="#BCBCBC" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
          <input placeholder="Search guest name or phone…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', padding:'0.55rem 0.75rem 0.55rem 2rem', border:'1.5px solid #E5E0D8', borderRadius:'0.75rem', outline:'none', boxSizing:'border-box' }}/>
        </div>
        <div style={{ display:'flex', gap:'0.375rem', flexWrap:'wrap' }}>
          {filterPills.map(p=>(
            <button key={p.id} onClick={()=>setFilter(p.id)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, padding:'0.35rem 0.75rem', borderRadius:100, border:'1.5px solid', cursor:'pointer', background:filter===p.id?'#1A1A1A':'white', borderColor:filter===p.id?'#1A1A1A':'#E5E0D8', color:filter===p.id?'white':'#6B6B6B' }}>
              {p.label}
            </button>
          ))}
        </div>
        <button onClick={exportCSV} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', fontWeight:700, color:'#6B6B6B', background:'white', border:'1.5px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.5rem 0.875rem', cursor:'pointer', whiteSpace:'nowrap' }}>
          ↓ Export
        </button>
      </div>

      <BulkActionBar filter={filter} filtered={bulkGuests} onOpen={onOpenBulk}/>

      {/* Table */}
      <div style={{ overflowX:'auto', borderRadius:'0.875rem', border:'1px solid #E5E0D8' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem' }}>
          <thead>
            <tr style={{ background:'#F8F4F0', borderBottom:'2px solid #EDE8E0' }}>
              <th style={{ padding:'0.625rem 0.75rem', width:36 }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor:'#C4826A', cursor:'pointer' }}/>
              </th>
              {['Guest','Yards','Amount','Collection Point','Status',''].map((h,i)=>(
                <th key={i} onClick={h==='Yards'||h==='Amount'?()=>toggleSort(h==='Yards'?'yards':'amount'):undefined}
                  style={{ padding:'0.625rem 0.75rem', textAlign:'left', color:'#6B5B52', fontWeight:700, fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap', cursor:h==='Yards'||h==='Amount'?'pointer':'default', userSelect:'none' }}>
                  <span style={{ display:'flex', alignItems:'center', gap:'0.25rem' }}>
                    {h}
                    {(h==='Yards'||h==='Amount') && (
                      sortField===(h==='Yards'?'yards':'amount')
                        ? (sortDir==='asc'?<ChevronUp size={12}/>:<ChevronDown size={12}/>)
                        : <ChevronDown size={12} color="#BCBCBC"/>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 && (
              <tr><td colSpan={7} style={{ padding:'2rem', textAlign:'center', color:'#BCBCBC', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem' }}>No orders match your filters.</td></tr>
            )}
            {filtered.map(o=>(
              <tr key={o.id} style={{ borderBottom:'1px solid #F3F0EA', background:selected.has(o.id)?'rgba(196,130,106,0.04)':'white' }}>
                <td style={{ padding:'0.625rem 0.75rem' }}>
                  <input type="checkbox" checked={selected.has(o.id)} onChange={()=>toggleOne(o.id)} style={{ accentColor:'#C4826A', cursor:'pointer' }}/>
                </td>
                <td style={{ padding:'0.625rem 0.75rem' }}>
                  <p style={{ fontWeight:700, color:'#1A1A1A', margin:0, whiteSpace:'nowrap' }}>{o.guest_name}</p>
                  {o.guest_phone && <p style={{ fontSize:'0.7rem', color:'#9B9B9B', margin:0 }}>{o.guest_phone}</p>}
                  {o.is_group && <span style={{ fontSize:'0.63rem', color:'#C4826A', fontWeight:700 }}>Group · {(o.group_names||[]).length} people</span>}
                  {remindedSet.has(o.id) && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:700, color:'#9BAF9B', background:'rgba(155,175,155,0.12)', borderRadius:100, padding:'1px 7px' }}>Reminded today</span>}
                </td>
                <td style={{ padding:'0.625rem 0.75rem', color:'#6B6B6B' }}>{o.yards} yds</td>
                <td style={{ padding:'0.625rem 0.75rem', fontWeight:700, color:'#1A1A1A' }}>₦{(o.amount||0).toLocaleString()}</td>
                <td style={{ padding:'0.625rem 0.75rem', color:'#6B6B6B', fontSize:'0.75rem', maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.collection_point||'—'}</td>
                <td style={{ padding:'0.625rem 0.75rem' }}><StatusPill status={o.status}/></td>
                <td style={{ padding:'0.625rem 0.75rem' }}>
                  <button onClick={(e)=>handleOpenMenu(o.id, e)} style={{ background:'none', border:'none', cursor:'pointer', padding:4, borderRadius:'0.5rem', color:'#9B9B9B' }}>
                    <MoreVertical size={16}/>
                  </button>
                  {openMenu===o.id && (
                    <div ref={menuRef} style={{ position:'fixed', top:menuPos.top, right:menuPos.right, background:'white', border:'1.5px solid #E5E0D8', borderRadius:'0.875rem', boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:9999, minWidth:190, padding:'0.375rem' }}>
                      {[
                        o.status==='paid'     && { label:'Mark Collected',     action:()=>{ if(window.confirm(`Mark ${o.guest_name} as collected?`)) onUpdateStatus(o,'collected') } },
                        o.status==='collected'&& { label:'Mark Uncollected',   action:()=>{ if(window.confirm(`Mark ${o.guest_name} as uncollected?`)) onUpdateStatus(o,'paid') } },
                        o.guest_phone && (o.status==='paid'||o.status==='collected') && { label:'Remind to Collect', action:()=>window.open(collectWaUrl(o, wedding), '_blank') },
                        o.guest_phone && o.status==='pending' && { label:'Remind to Pay', action:()=>window.open(payWaUrl(o), '_blank') },
                      ].filter(Boolean).map((item,i) => item && (
                        <button key={i} onClick={()=>{ item.action(); setOpenMenu(null) }} style={{ display:'block', width:'100%', textAlign:'left', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:600, color:item.danger?'#DC2626':'#1A1A1A', background:'none', border:'none', padding:'0.5rem 0.75rem', borderRadius:'0.5rem', cursor:'pointer' }}
                          onMouseEnter={e=>e.currentTarget.style.background='#F8F4F0'} onMouseLeave={e=>e.currentTarget.style.background='none'}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{ position:'sticky', bottom:16, marginTop:'1rem', background:'#1A1A1A', borderRadius:'1rem', padding:'0.875rem 1.25rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem', boxShadow:'0 8px 24px rgba(0,0,0,0.2)', flexWrap:'wrap' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'rgba(255,255,255,0.7)' }}>{selected.size} selected</span>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={()=>onSendReminder(filtered.filter(o=>selected.has(o.id)))} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', fontWeight:700, color:'#1A1A1A', background:'#F4B300', border:'none', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer' }}>
              Send Reminder to Selected
            </button>
            <button onClick={()=>{ if(window.confirm(`Mark ${selected.size} orders as collected?`)) { filtered.filter(o=>selected.has(o.id)&&o.status==='paid').forEach(o=>onUpdateStatus(o,'collected')) } }} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', fontWeight:700, color:'white', background:'#9BAF9B', border:'none', borderRadius:100, padding:'0.5rem 1rem', cursor:'pointer' }}>
              Mark Selected as Collected
            </button>
            <button onClick={()=>setSelected(new Set())} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:100, padding:'0.5rem 0.75rem', cursor:'pointer', color:'white' }}>
              <X size={14}/>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// TABLET VIEW
// ────────────────────────────────────────────────────────────
function TabletView({ filtered, search, setSearch, filter, setFilter, filterPills, onUpdateStatus, onSendReminder, updatingOrder, remindedSet, bulkGuests, onOpenBulk, wedding }) {
  const [expanded, setExpanded] = useState(null)

  return (
    <div>
      {/* Search */}
      <div style={{ position:'relative', marginBottom:'0.75rem' }}>
        <Search size={14} color="#BCBCBC" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
        <input placeholder="Search guest…" value={search} onChange={e=>setSearch(e.target.value)}
          style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', padding:'0.6rem 0.75rem 0.6rem 2rem', border:'1.5px solid #E5E0D8', borderRadius:'0.875rem', outline:'none', boxSizing:'border-box' }}/>
      </div>
      {/* Filter pills */}
      <div style={{ display:'flex', gap:'0.375rem', flexWrap:'wrap', marginBottom:'0.875rem' }}>
        {filterPills.map(p=>(
          <button key={p.id} onClick={()=>setFilter(p.id)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, padding:'0.3rem 0.7rem', borderRadius:100, border:'1.5px solid', cursor:'pointer', background:filter===p.id?'#1A1A1A':'white', borderColor:filter===p.id?'#1A1A1A':'#E5E0D8', color:filter===p.id?'white':'#6B6B6B' }}>
            {p.label}
          </button>
        ))}
      </div>

      <BulkActionBar filter={filter} filtered={bulkGuests} onOpen={onOpenBulk}/>

      {/* Table */}
      <div style={{ borderRadius:'0.875rem', border:'1px solid #E5E0D8', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem' }}>
          <thead>
            <tr style={{ background:'#F8F4F0', borderBottom:'2px solid #EDE8E0' }}>
              {['Guest','Amount','Status',''].map(h=>(
                <th key={h} style={{ padding:'0.625rem 0.875rem', textAlign:'left', color:'#6B5B52', fontWeight:700, fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 && (
              <tr><td colSpan={4} style={{ padding:'2rem', textAlign:'center', color:'#BCBCBC', fontSize:'0.82rem' }}>No orders match your filters.</td></tr>
            )}
            {filtered.map(o=>(
              <>
                <tr key={o.id} onClick={()=>setExpanded(expanded===o.id?null:o.id)} style={{ borderBottom:expanded===o.id?'none':'1px solid #F3F0EA', cursor:'pointer', background:expanded===o.id?'rgba(196,130,106,0.03)':'white' }}>
                  <td style={{ padding:'0.75rem 0.875rem' }}>
                    <p style={{ fontWeight:700, color:'#1A1A1A', margin:0 }}>{o.guest_name}</p>
                    {o.guest_phone && <p style={{ fontSize:'0.7rem', color:'#9B9B9B', margin:0 }}>{o.guest_phone}</p>}
                  </td>
                  <td style={{ padding:'0.75rem 0.875rem', fontWeight:700, color:'#1A1A1A', whiteSpace:'nowrap' }}>₦{(o.amount||0).toLocaleString()}</td>
                  <td style={{ padding:'0.75rem 0.875rem' }}><StatusPill status={o.status}/></td>
                  <td style={{ padding:'0.75rem 0.875rem', color:'#BCBCBC' }}>
                    {expanded===o.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                  </td>
                </tr>
                {expanded===o.id && (
                  <tr key={`${o.id}-expanded`} style={{ borderBottom:'1px solid #F3F0EA' }}>
                    <td colSpan={4} style={{ padding:'0 0.875rem 0.875rem' }}>
                      <div style={{ background:'#FAF5EC', borderRadius:'0.75rem', padding:'0.875rem 1rem' }}>
                        <div style={{ display:'flex', gap:'1.5rem', marginBottom:'0.875rem', flexWrap:'wrap' }}>
                          <div>
                            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'#9B9B9B', fontWeight:700, textTransform:'uppercase', margin:'0 0 0.15rem' }}>Yards</p>
                            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#1A1A1A', fontWeight:600, margin:0 }}>{o.yards} yards</p>
                          </div>
                          {o.collection_point && (
                            <div>
                              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'#9B9B9B', fontWeight:700, textTransform:'uppercase', margin:'0 0 0.15rem' }}>Collection Point</p>
                              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#1A1A1A', fontWeight:600, margin:0 }}>{o.collection_point}</p>
                            </div>
                          )}
                          <div>
                            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'#9B9B9B', fontWeight:700, textTransform:'uppercase', margin:'0 0 0.15rem' }}>Date</p>
                            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#1A1A1A', fontWeight:600, margin:0 }}>{new Date(o.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</p>
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                          {o.status==='paid'      && <button onClick={()=>{ if(window.confirm(`Mark ${o.guest_name} as collected?`)) onUpdateStatus(o,'collected') }} disabled={!!updatingOrder} style={pillBtn('#9BAF9B','white')}>Mark Collected</button>}
                          {o.status==='collected' && <button onClick={()=>{ if(window.confirm(`Mark as uncollected?`)) onUpdateStatus(o,'paid') }} disabled={!!updatingOrder} style={pillBtn('#F3F0EA','#6B6B6B')}>Mark Uncollected</button>}
                          {o.guest_phone && (o.status==='paid'||o.status==='collected') && <button onClick={()=>window.open(collectWaUrl(o, wedding), '_blank')} style={pillBtn('#25D366','white')}>Remind to Collect</button>}
                          {o.guest_phone && o.status==='pending' && <button onClick={()=>window.open(payWaUrl(o), '_blank')} style={pillBtn('#F4B300','white')}>Remind to Pay</button>}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// MOBILE VIEW
// ────────────────────────────────────────────────────────────
function MobileView({ filtered, search, setSearch, filter, setFilter, filterPills, onUpdateStatus, onSendReminder, updatingOrder, remindedSet, bulkGuests, onOpenBulk, wedding }) {
  const [sheet, setSheet]           = useState(null) // order for bottom sheet
  const [selecting, setSelecting]   = useState(false)
  const [selected, setSelected]     = useState(new Set())
  const longPressRef                = useRef(null)

  const startLongPress = (o) => {
    longPressRef.current = setTimeout(() => {
      setSelecting(true)
      setSelected(new Set([o.id]))
    }, 500)
  }
  const endLongPress = () => { clearTimeout(longPressRef.current) }

  const toggleSelect = (id) => {
    setSelected(prev => { const s = new Set(prev); s.has(id)?s.delete(id):s.add(id); return s })
  }

  const grouped = useMemo(() => {
    if (filtered.length < 50) return null
    const g = {}
    filtered.forEach(o => { const k = (o.guest_name||'?')[0].toUpperCase(); if(!g[k]) g[k]=[]; g[k].push(o) })
    return g
  }, [filtered])

  const rowsToRender = grouped ? Object.values(grouped).flat() : filtered

  return (
    <div style={{ position:'relative' }}>
      {/* Sticky header */}
      <div style={{ position:'sticky', top:0, zIndex:20, background:'white', paddingBottom:'0.625rem', marginBottom:'0.5rem' }}>
        <div style={{ position:'relative', marginBottom:'0.5rem' }}>
          <Search size={14} color="#BCBCBC" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
          <input placeholder="Search guest…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', padding:'0.6rem 0.75rem 0.6rem 2rem', border:'1.5px solid #E5E0D8', borderRadius:'0.875rem', outline:'none', boxSizing:'border-box' }}/>
        </div>
        <div style={{ display:'flex', gap:'0.35rem', overflowX:'auto', paddingBottom:2 }}>
          {filterPills.map(p=>(
            <button key={p.id} onClick={()=>setFilter(p.id)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', fontWeight:600, padding:'0.3rem 0.625rem', borderRadius:100, border:'1.5px solid', cursor:'pointer', flexShrink:0, background:filter===p.id?'#1A1A1A':'white', borderColor:filter===p.id?'#1A1A1A':'#E5E0D8', color:filter===p.id?'white':'#6B6B6B' }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <BulkActionBar filter={filter} filtered={bulkGuests} onOpen={onOpenBulk}/>

      {/* Row list */}
      {rowsToRender.length===0 && (
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#BCBCBC', textAlign:'center', padding:'2rem' }}>No orders match your filters.</p>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:'1px', background:'#F3F0EA', borderRadius:'0.875rem', overflow:'hidden' }}>
        {rowsToRender.map(o=>(
          <div key={o.id}
            onTouchStart={()=>startLongPress(o)} onTouchEnd={endLongPress} onMouseDown={()=>startLongPress(o)} onMouseUp={endLongPress}
            onClick={()=>{ if(selecting) toggleSelect(o.id); else setSheet(o) }}
            style={{ background: selected.has(o.id)?'rgba(196,130,106,0.07)':'white', padding:'0.875rem 1rem', cursor:'pointer', display:'flex', flexDirection:'column', gap:'0.25rem' }}>
            {/* Line 1 */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.5rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', minWidth:0 }}>
                {selecting && (
                  <input type="checkbox" checked={selected.has(o.id)} onChange={()=>toggleSelect(o.id)} onClick={e=>e.stopPropagation()} style={{ accentColor:'#C4826A', flexShrink:0 }}/>
                )}
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.875rem', color:'#1A1A1A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.guest_name}</span>
                {o.is_group && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:700, color:'#C4826A', background:'rgba(196,130,106,0.1)', borderRadius:100, padding:'1px 6px', flexShrink:0 }}>Group</span>}
                {remindedSet.has(o.id) && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.58rem', fontWeight:700, color:'#9BAF9B', background:'rgba(155,175,155,0.12)', borderRadius:100, padding:'1px 6px', flexShrink:0 }}>✓</span>}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexShrink:0 }}>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.875rem', color:'#1A1A1A' }}>₦{(o.amount||0).toLocaleString()}</span>
                <StatusDot status={o.status}/>
              </div>
            </div>
            {/* Line 2 */}
            <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B' }}>{o.yards} yards</span>
              {o.collection_point && <><span style={{ color:'#BCBCBC', fontSize:'0.6rem' }}>·</span><span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.collection_point}</span></>}
            </div>
          </div>
        ))}
      </div>

      {/* Alphabetical index for 50+ */}
      {grouped && (
        <div style={{ position:'fixed', right:4, top:'50%', transform:'translateY(-50%)', display:'flex', flexDirection:'column', gap:1, zIndex:30 }}>
          {Object.keys(grouped).sort().map(letter=>(
            <button key={letter} onClick={()=>{ const el=document.getElementById(`alpha-${letter}`); el?.scrollIntoView({behavior:'smooth'}) }}
              style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.55rem', fontWeight:700, color:'#C4826A', background:'none', border:'none', cursor:'pointer', padding:'1px 3px' }}>
              {letter}
            </button>
          ))}
        </div>
      )}

      {/* Bulk bar */}
      {selecting && selected.size>0 && (
        <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'#1A1A1A', padding:'0.875rem 1rem', display:'flex', gap:'0.5rem', alignItems:'center', justifyContent:'space-between', zIndex:40 }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'rgba(255,255,255,0.7)' }}>{selected.size} selected</span>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={()=>onSendReminder(filtered.filter(o=>selected.has(o.id)))} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#1A1A1A', background:'#F4B300', border:'none', borderRadius:100, padding:'0.5rem 0.875rem', cursor:'pointer' }}>Remind</button>
            <button onClick={()=>{ setSelecting(false); setSelected(new Set()) }} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:100, padding:'0.5rem 0.75rem', cursor:'pointer', color:'white' }}><X size={14}/></button>
          </div>
        </div>
      )}

      {/* Bottom sheet */}
      {sheet && (
        <div style={{ position:'fixed', inset:0, zIndex:50 }} onClick={()=>setSheet(null)}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)' }}/>
          <div onClick={e=>e.stopPropagation()} style={{ position:'absolute', bottom:0, left:0, right:0, background:'white', borderRadius:'1.25rem 1.25rem 0 0', padding:'0 0 env(safe-area-inset-bottom)', maxHeight:'85vh', overflowY:'auto' }}>
            {/* Handle */}
            <div style={{ display:'flex', justifyContent:'center', padding:'0.75rem 0 0' }}>
              <div style={{ width:36, height:4, background:'#E5E0D8', borderRadius:100 }}/>
            </div>
            <div style={{ padding:'1rem 1.25rem 1.5rem' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'1.25rem' }}>
                <div>
                  <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#1A1A1A', margin:'0 0 0.2rem' }}>{sheet.guest_name}</h3>
                  {sheet.is_group && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#C4826A', fontWeight:700 }}>Group Order · {(sheet.group_names||[]).length} people</span>}
                </div>
                <StatusPill status={sheet.status}/>
              </div>

              {/* Details grid */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.875rem', marginBottom:'1.25rem' }}>
                {[
                  { label:'Yards Ordered', value:`${sheet.yards} yards` },
                  { label:'Total Amount', value:`₦${(sheet.amount||0).toLocaleString()}` },
                  { label:'Collection Point', value:sheet.collection_point||'—' },
                  { label:'Order Date', value:new Date(sheet.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) },
                  sheet.guest_phone && { label:'Phone', value:sheet.guest_phone },
                ].filter(Boolean).map(d=>(
                  <div key={d.label} style={{ background:'#FAF5EC', borderRadius:'0.75rem', padding:'0.75rem' }}>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', color:'#9B9B9B', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', margin:'0 0 0.2rem' }}>{d.label}</p>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#1A1A1A', fontWeight:600, margin:0 }}>{d.value}</p>
                  </div>
                ))}
              </div>

              {/* Contact buttons */}
              {sheet.guest_phone && (
                <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.875rem' }}>
                  <a href={`https://wa.me/${sheet.guest_phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem', background:'#25D366', color:'white', borderRadius:'0.75rem', padding:'0.75rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', textDecoration:'none' }}>
                    <MessageCircle size={14}/> WhatsApp
                  </a>
                  <a href={`tel:${sheet.guest_phone}`} style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem', background:'#F3F0EA', color:'#1A1A1A', borderRadius:'0.75rem', padding:'0.75rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', textDecoration:'none' }}>
                    <Phone size={14}/> Call
                  </a>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                {sheet.status==='paid'      && <button onClick={()=>{ if(window.confirm(`Mark ${sheet.guest_name} as collected?`)){ onUpdateStatus(sheet,'collected'); setSheet(null) }}} style={sheetBtn('#9BAF9B','white')}>✓ Mark as Collected</button>}
                {sheet.status==='collected' && <button onClick={()=>{ if(window.confirm(`Mark as uncollected?`)){ onUpdateStatus(sheet,'paid'); setSheet(null) }}} style={sheetBtn('#F3F0EA','#6B6B6B')}>Mark Uncollected</button>}
                {sheet.guest_phone && (sheet.status==='paid'||sheet.status==='collected') && <button onClick={()=>window.open(collectWaUrl(sheet, wedding), '_blank')} style={sheetBtn('#25D366','white')}>📦 Remind to Collect</button>}
                {sheet.guest_phone && sheet.status==='pending' && <button onClick={()=>window.open(payWaUrl(sheet), '_blank')} style={sheetBtn('#F4B300','white')}>Remind to Pay</button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Utility helpers ───────────────────────────────────────────
// WhatsApp "your fabric is ready, come collect" reminder
const collectWaUrl = (o, wedding) => {
  if (!o.guest_phone) return null
  const clean = o.guest_phone.replace(/\D/g,'').replace(/^0/,'234')
  const where = o.collection_point ? ` at ${o.collection_point}` : ''
  const msg = `Hi ${o.guest_name||'there'}, your Aso Ebi for ${wedding?.partner1_name||''} & ${wedding?.partner2_name||''}'s wedding is ready for pickup${where}. Please come and collect it. Thank you! 🧵`
  return `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`
}
const payWaUrl = (o) => {
  if (!o.guest_phone) return null
  const clean = o.guest_phone.replace(/\D/g,'').replace(/^0/,'234')
  const msg = `Hi ${o.guest_name||'there'}, please complete your Aso Ebi payment to confirm your order. Thank you!`
  return `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`
}

// ── Utility style fns ─────────────────────────────────────────
const pillBtn = (bg, color) => ({
  fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700,
  color, background:bg, border:'none', borderRadius:100, padding:'5px 12px', cursor:'pointer'
})
const sheetBtn = (bg, color) => ({
  fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', fontWeight:700,
  color, background:bg, border:'none', borderRadius:'0.75rem', padding:'0.875rem', cursor:'pointer', width:'100%'
})
